import React from 'react';
import { useDeepCompareMemoize } from './useDeepCompareMemoize';

/**
 * C (Create) U (Update) D (Delete) change manager
 *
 * Buffers granular changes (e.g. dragging shapes on the drawing canvas) in
 * local state, and only notifies the owner (onCreate / onUpdate / onDelete)
 * when saveChanges is called — for instance when leaving draw mode — so that
 * every small movement doesn't become a network call.
 *
 * Sync contract with the controlled `state` prop (usually redux):
 *
 *  - Down: when `state` changes it is merged into localState, but entities
 *    with unsaved changes or with a save still in flight are protected — the
 *    local version wins. This is what stops a stale server echo or a delta
 *    poll from snapping an annotation back while it is being edited.
 *
 *  - Up: saveChanges flushes the buffered changes through the callbacks.
 *    Callbacks may return a promise; while it is pending the entity stays
 *    protected. If it rejects, the entity is restored from the last known
 *    `state` (a failed add disappears, a failed update or delete reverts)
 *    and onSaveError is called so the failure can be surfaced to the user.
 */
export function useCUDChangeManager<DataType>({
  getObjectId,
  ...props
}: {
  getObjectId: (data: DataType) => string;
  onCreate: (data: DataType) => void | Promise<unknown>;
  onUpdate: (data: DataType) => void | Promise<unknown>;
  onDelete: (id: string) => void | Promise<unknown>;
  onSaveError?: (error: unknown, entityId: string) => void;
  /**
   * Use this prop to control the state of the change manager.
   * When this is updated, it is merged into localState (protected entities
   * excepted — see the sync contract above).
   */
  state?: DataType[];
}) {
  const [localState, setLocalState] = React.useState(props.state || []);

  const changes = React.useRef({
    added: [] as DataType[],
    updated: [] as DataType[],
    deleted: [] as string[],
  });

  // Saves dispatched but not yet acknowledged, by entity id. An entity can
  // have overlapping in-flight saves, hence a count rather than a set.
  const inFlight = React.useRef(new Map<string, number>());

  // Latest server truth, for reverting failed saves. A ref so the promise
  // callbacks in saveChanges don't close over a stale array.
  const serverStateRef = React.useRef(props.state);
  serverStateRef.current = props.state;

  const getProtectedIds = React.useCallback(() => {
    const ids = new Set<string>();
    changes.current.added.forEach((obj) => ids.add(getObjectId(obj)));
    changes.current.updated.forEach((obj) => ids.add(getObjectId(obj)));
    changes.current.deleted.forEach((id) => ids.add(id));
    inFlight.current.forEach((_, id) => ids.add(id));
    return ids;
  }, [getObjectId]);

  /*
    Down-sync. Merge rules per entity:
      - unprotected                -> take the incoming version
      - protected, present locally -> keep the local version
      - protected, absent locally  -> locally deleted, keep it deleted
      - protected, local-only      -> locally added, keep it
  */
  React.useEffect(() => {
    const incoming = props.state;
    if (!incoming) return;
    setLocalState((local) => {
      const protectedIds = getProtectedIds();
      if (protectedIds.size === 0) return incoming;

      const localById = new Map(local.map((obj) => [getObjectId(obj), obj]));
      const incomingIds = new Set<string>();
      const next: DataType[] = [];
      for (const obj of incoming) {
        const id = getObjectId(obj);
        incomingIds.add(id);
        if (!protectedIds.has(id)) {
          next.push(obj);
        } else if (localById.has(id)) {
          next.push(localById.get(id) as DataType);
        }
      }
      const localOnly = local.filter(
        (obj) =>
          !incomingIds.has(getObjectId(obj)) &&
          protectedIds.has(getObjectId(obj))
      );
      return [...localOnly, ...next];
    });
  }, [useDeepCompareMemoize(props.state)]);

  // Restore an entity from the last known server truth after a failed save.
  // Skipped when the entity has been re-edited or has another save in
  // flight — that save's own outcome supersedes this one.
  const revertFromServerState = React.useCallback(
    (entityId: string) => {
      const server = serverStateRef.current;
      if (!server) return;
      setLocalState((local) => {
        if (getProtectedIds().has(entityId)) return local;
        const serverVersion = server.find(
          (obj) => getObjectId(obj) === entityId
        );
        const localIndex = local.findIndex(
          (obj) => getObjectId(obj) === entityId
        );
        if (serverVersion === undefined) {
          // Failed add: the entity never existed server-side.
          return localIndex === -1
            ? local
            : local.filter((_, index) => index !== localIndex);
        }
        if (localIndex === -1) {
          // Failed delete: bring it back.
          return [serverVersion, ...local];
        }
        // Failed update: snap back to the server version.
        const next = [...local];
        next[localIndex] = serverVersion;
        return next;
      });
    },
    [getObjectId, getProtectedIds]
  );

  const trackSave = React.useCallback(
    (entityId: string, save: () => void | Promise<unknown>) => {
      let result: void | Promise<unknown>;
      try {
        result = save();
      } catch (error) {
        revertFromServerState(entityId);
        props.onSaveError?.(error, entityId);
        return;
      }
      if (!result || typeof (result as Promise<unknown>).then !== 'function') {
        return;
      }
      inFlight.current.set(entityId, (inFlight.current.get(entityId) || 0) + 1);
      const settle = () => {
        const count = (inFlight.current.get(entityId) || 1) - 1;
        if (count <= 0) inFlight.current.delete(entityId);
        else inFlight.current.set(entityId, count);
      };
      (result as Promise<unknown>).then(
        () => settle(),
        (error) => {
          settle();
          revertFromServerState(entityId);
          props.onSaveError?.(error, entityId);
        }
      );
    },
    [revertFromServerState, props.onSaveError]
  );

  const saveChanges = React.useCallback(() => {
    const { added, updated, deleted } = changes.current;
    changes.current = {
      added: [],
      updated: [],
      deleted: [],
    };
    added.forEach((obj) =>
      trackSave(getObjectId(obj), () => props.onCreate(obj))
    );
    updated.forEach((obj) =>
      trackSave(getObjectId(obj), () => props.onUpdate(obj))
    );
    deleted.forEach((id) => trackSave(id, () => props.onDelete(id)));
  }, [props.onCreate, props.onUpdate, props.onDelete, getObjectId, trackSave]);

  /*
    Add Logic:
      - Additions are fairly simple. Just add to the added queue
  */
  const handleAdd = React.useCallback(
    (objToAdd: DataType) => {
      changes.current.added.push(objToAdd);
      setLocalState((state) => [objToAdd, ...state]);
    },
    [getObjectId]
  );

  /*
    Update Logic:
      - Check to see if the same object is in the added queue, if so just update it there, if not, *upsert* it in the updated queue
  */
  const handleUpdate = React.useCallback(
    (objToUpdate: DataType) => {
      const added = changes.current.added.find((addedObj) =>
        getObjectId(addedObj) === getObjectId(objToUpdate) ? true : false
      );
      if (!added) {
        changes.current.updated = [
          objToUpdate,
          ...changes.current.updated.filter((updatedObj) =>
            getObjectId(updatedObj) === getObjectId(objToUpdate) ? false : true
          ),
        ];
      } else {
        changes.current.added = [
          objToUpdate,
          ...changes.current.added.filter((addedObj) =>
            getObjectId(addedObj) === getObjectId(objToUpdate) ? false : true
          ),
        ];
      }

      setLocalState((state) =>
        state.map((obj) =>
          getObjectId(obj) === getObjectId(objToUpdate) ? objToUpdate : obj
        )
      );
    },
    [getObjectId]
  );

  /*
    Delete Logic:
      - If the object is in the added queue, remove it since no one needs to be notified of any changes (its as if nothing happened)
      - else if the object is in the updated queue, remove it from there but don't return
      - Lastly, if you've reached this far, add the object ID to the deleted queue
  */
  const handleDelete = React.useCallback(
    (idToDelete: string) => {
      const added = changes.current.added.find((addedObj) =>
        getObjectId(addedObj) === idToDelete ? true : false
      );

      if (added) {
        changes.current.added = changes.current.added.filter(
          (addedObj) => getObjectId(addedObj) !== idToDelete
        );
      } else {
        const updated = changes.current.updated.find((updatedObj) =>
          getObjectId(updatedObj) === idToDelete ? true : false
        );

        if (updated) {
          changes.current.updated = changes.current.updated.filter(
            (updatedObj) => getObjectId(updatedObj) !== idToDelete
          );
        }
        changes.current.deleted.push(idToDelete);
      }
      setLocalState((state) =>
        state.filter((obj) => (getObjectId(obj) === idToDelete ? false : true))
      );
    },
    [getObjectId]
  );

  const handleUpsert = React.useCallback(
    (obj: DataType) => {
      if (
        changes.current.added.find(
          (added) => getObjectId(added) === getObjectId(obj)
        )
      ) {
        return handleUpdate(obj);
      } else {
        return handleAdd(obj);
      }
    },
    [handleAdd, handleUpdate, getObjectId]
  );

  return {
    handleAdd,
    handleDelete,
    handleUpdate,
    handleUpsert,
    saveChanges,
    localState,
  };
}
